import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, ExternalLink, LogOut, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";
import { ADMIN, formatDetail } from "../lib/adminApi";
import { DIMENSIONS, ROLES, SCENARIOS, VERDICTS } from "../lib/bopta";
import { AdminCompareDialog, BothLensesTag } from "../components/AdminCompare";

const PAGE = 25;
const ANY = "all";
const fmtDate = (s) => new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
const val = (v) => (v === ANY ? "" : v);
const useDebounced = (value, ms) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
};

const workingLabel = (w) => ({ true: "Working", false: "Past role" }[String(w)] || "—");
const teamSub = (t) => [t.org, t.manager_name].filter(Boolean).join(" · ");
const toParams = (f) => ({ q: f.q.trim(), mode: val(f.mode), role: val(f.role), verdict: val(f.verdict), updates: val(f.updates), scenario: val(f.scenario) });

const downloadCsv = async (params, path = "/admin/export.csv", name = "mpwrd-assessments.csv") => {
  const r = await ADMIN.get(path, { params, responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};

const useAdminData = (f, page) => {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [data, setData] = useState(null);
  const [teams, setTeams] = useState([]);
  const load = useCallback(() => {
    ADMIN.get("/admin/assessments", { params: { ...toParams(f), skip: page * PAGE, limit: PAGE } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatDetail(e.response?.data?.detail)));
  }, [f, page]);
  useEffect(() => {
    ADMIN.get("/auth/me").then((r) => setUser(r.data)).catch(() => nav("/admin/login"));
  }, [nav]);
  useEffect(() => {
    if (!user) return;
    load();
    ADMIN.get("/admin/teams").then((r) => setTeams(r.data));
  }, [user, load]);
  return { user, data, teams, load };
};

const Header = ({ email, onExport, onExportOptin, onLogout }) => (
  <div className="flex flex-wrap items-end justify-between gap-4">
    <div><p className="kicker">Admin</p><h1 className="mt-2 text-4xl font-extrabold">All assessments</h1><p className="mt-1 text-sm muted">Signed in as {email}</p></div>
    <div className="flex gap-2">
      <button className="btn btn-line" onClick={onExport} data-testid="admin-export"><Download className="h-4 w-4" /> Export CSV</button>
      <button className="btn btn-line" onClick={onExportOptin} data-testid="admin-export-optin"><Download className="h-4 w-4" /> Opt-in contacts</button>
      <button className="btn btn-line" onClick={onLogout} data-testid="admin-logout"><LogOut className="h-4 w-4" /> Sign out</button>
    </div>
  </div>
);

const StatsRow = ({ st }) => (
  <section className="mt-8 grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
    <Stat label="Assessments" value={st.total} testid="admin-stat-total" />
    <Stat label="Staff lens" value={st.staff} testid="admin-stat-staff" />
    <Stat label="Avg autonomy" value={st.avg_overall == null ? null : Math.round(st.avg_overall)} testid="admin-stat-autonomy" />
    <Stat label="Avg bandwidth" value={st.avg_bandwidth == null ? null : Math.round(st.avg_bandwidth)} testid="admin-stat-bandwidth" />
    <Stat label="Untapped" value={st.untapped} testid="admin-stat-untapped" />
    <Stat label="Opted in" value={st.opted_in} testid="admin-stat-optin" />
  </section>
);

const ROLE_OPTS = ROLES.map((r) => [r.v, r.l]);
const VERDICT_OPTS = Object.entries(VERDICTS).map(([k, v]) => [k, v[0]]);
const MODE_OPTS = [["staff", "Staff"], ["manager", "Manager"], ["both", "Took both lenses"]];
const UPDATES_OPTS = [["yes", "Opted in to updates"], ["no", "Not opted in"]];
const SCENARIO_OPTS = Object.keys(SCENARIOS).sort().map((k) => [k, `${k} · ${SCENARIOS[k].name}`]);

const Filters = ({ f, setFilter, total }) => (
  <div className="flex flex-wrap items-center gap-2">
    <div className="relative"><Search className="absolute left-3 top-3 h-4 w-4 muted" /><Input className="h-10 w-64 bg-white pl-9" placeholder="Name, email, result ID or team code" value={f.q} onChange={(e) => setFilter("q", e.target.value)} data-testid="admin-search" /></div>
    <FilterSelect value={f.mode} onChange={(v) => setFilter("mode", v)} placeholder="All lenses" options={MODE_OPTS} testid="admin-filter-mode" />
    <FilterSelect value={f.role} onChange={(v) => setFilter("role", v)} placeholder="All roles" options={ROLE_OPTS} testid="admin-filter-role" />
    <FilterSelect value={f.verdict} onChange={(v) => setFilter("verdict", v)} placeholder="All verdicts" options={VERDICT_OPTS} testid="admin-filter-verdict" />
    <FilterSelect value={f.scenario} onChange={(v) => setFilter("scenario", v)} placeholder="All scenarios" options={SCENARIO_OPTS} testid="admin-filter-scenario" />
    <FilterSelect value={f.updates} onChange={(v) => setFilter("updates", v)} placeholder="Any updates choice" options={UPDATES_OPTS} testid="admin-filter-updates" />
    <span className="ml-auto text-sm muted" data-testid="admin-total">{total} {total === 1 ? "result" : "results"}</span>
  </div>
);

const Stat = ({ label, value, testid }) => (
  <div className="panel p-5" data-testid={testid}><p className="kicker">{label}</p><p className="display mt-2 text-3xl font-extrabold">{value ?? "—"}</p></div>
);

const FilterSelect = ({ value, onChange, placeholder, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-10 w-44 bg-white" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value={ANY}>{placeholder}</SelectItem>
      {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const Row = ({ r, onDelete, onCompare }) => (
  <tr className="border-t border-[var(--line)] align-top" data-testid={`admin-row-${r.id}`}>
    <td className="py-3 pr-3 text-xs muted">{fmtDate(r.created_at)}</td>
    <td className="pr-3" data-testid={`admin-person-${r.id}`}>{r.name || <span className="muted">Not provided</span>}<span className="block text-xs muted">{r.email || ""}</span><span className="block text-xs muted">{r.phone || ""}</span></td>
    <td className="pr-3 text-xs">{workingLabel(r.working)}{r.updates_opt_in && <span className="mt-1 block w-fit rounded bg-[var(--tint)] px-1.5 text-[var(--teal)]">Updates ✓</span>}</td>
    <td className="pr-3 capitalize">{r.mode}{r.both_lenses && r.latest && <BothLensesTag r={r} onCompare={onCompare} />}</td>
    <td className="pr-3">{r.role}<span className="block text-xs muted">→ {r.manager}</span></td>
    <td className="pr-3 font-bold">{Math.round(r.overall)}<span className="block text-xs font-normal muted">L{r.level} {r.level_name}</span></td>
    <td className="pr-3 text-xs">{DIMENSIONS.map((d) => `${d.k} ${Math.round(r.dims[d.k])}`).join(" · ")}</td>
    <td className="pr-3">{r.bandwidth == null ? "—" : Math.round(r.bandwidth)}<span className="block text-xs muted">{r.band || ""}</span></td>
    <td className="pr-3 text-xs">{r.verdict ? VERDICTS[r.verdict][0] : "—"}{r.scenarios.length > 0 && <span className="block muted">Scenario {r.scenarios.join(", ")}</span>}</td>
    <td className="pr-3 text-xs">{r.team_name ? `${r.team_name} (${r.team_code})` : "—"}{r.round && <span className="block muted">Round {r.round}</span>}</td>
    <td className="whitespace-nowrap">
      <Link to={`/results/${r.id}`} target="_blank" className="mr-3 inline-flex text-[var(--teal)]" title="Open result" data-testid={`admin-open-${r.id}`}><ExternalLink className="h-4 w-4" /></Link>
      <button onClick={() => onDelete(r.id)} className="inline-flex text-[var(--orange)]" title="Delete" data-testid={`admin-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
    </td>
  </tr>
);

const Teams = ({ teams }) => (
  <div className="panel overflow-x-auto p-5" data-testid="admin-teams">
    <table className="w-full text-sm">
      <thead><tr className="text-left muted"><th className="py-2 font-normal">Team</th><th className="font-normal">Code</th><th className="font-normal">Round</th><th className="font-normal">Responses</th><th className="font-normal">Created</th><th /></tr></thead>
      <tbody>
        {teams.map((t) => (
          <tr key={t.code} className="border-t border-[var(--line)]" data-testid={`admin-team-${t.code}`}>
            <td className="py-3">{t.name}<span className="block text-xs muted">{teamSub(t)}</span></td>
            <td>{t.code}</td><td>{t.round}</td><td>{t.responses}</td><td className="text-xs muted">{t.created_at ? fmtDate(t.created_at) : "—"}</td>
            <td><Link to={`/team/${t.code}?admin=${t.admin_token}`} target="_blank" className="text-[var(--teal)]" data-testid={`admin-team-open-${t.code}`}>Dashboard ↗</Link></td>
          </tr>
        ))}
        {teams.length === 0 && <tr><td colSpan={6} className="py-6 text-center muted">No teams yet.</td></tr>}
      </tbody>
    </table>
  </div>
);

export default function Admin() {
  const nav = useNavigate();
  const [f, setF] = useState({ q: "", mode: ANY, role: ANY, verdict: ANY, updates: ANY, scenario: ANY });
  const [page, setPage] = useState(0);
  const [comparing, setComparing] = useState(null);
  const debounced = useDebounced(f, 300);
  const { user, data, teams, load } = useAdminData(debounced, page);

  const remove = async (id) => {
    if (!window.confirm("Delete this assessment permanently?")) return;
    await ADMIN.delete(`/admin/assessments/${id}`);
    toast.success("Assessment deleted");
    load();
  };
  const logout = async () => {
    await ADMIN.post("/auth/logout");
    nav("/admin/login");
  };
  const setFilter = (k, v) => { setPage(0); setF((p) => ({ ...p, [k]: v })); };

  if (!user || !data) return <div className="wrap space-y-4 py-16"><Skeleton className="h-12 w-1/3" /><Skeleton className="h-64 w-full" /></div>;
  const pages = Math.max(1, Math.ceil(data.total / PAGE));

  return (
    <div className="wrap py-12" data-testid="admin-page">
      <Header email={user.email} onExport={() => downloadCsv(toParams(f))} onExportOptin={() => downloadCsv({}, "/admin/optin.csv", "mpwrd-optin-contacts.csv")} onLogout={logout} />
      <StatsRow st={data.stats} />
      <Tabs defaultValue="assessments" className="mt-8">
        <TabsList>
          <TabsTrigger value="assessments" data-testid="admin-tab-assessments">Assessments</TabsTrigger>
          <TabsTrigger value="teams" data-testid="admin-tab-teams">Teams ({teams.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="assessments" className="mt-4">
          <Filters f={f} setFilter={setFilter} total={data.total} />
          <div className="panel mt-4 overflow-x-auto p-5">
            <table className="w-full min-w-[1250px] text-sm" data-testid="admin-table">
              <thead><tr className="text-left muted"><th className="py-2 font-normal">Date</th><th className="font-normal">Person</th><th className="font-normal">Working</th><th className="font-normal">Lens</th><th className="font-normal">Role</th><th className="font-normal">Autonomy</th><th className="font-normal">BOPTA</th><th className="font-normal">Bandwidth</th><th className="font-normal">Verdict</th><th className="font-normal">Team</th><th /></tr></thead>
              <tbody>
                {data.items.map((r) => <Row key={r.id} r={r} onDelete={remove} onCompare={setComparing} />)}
                {data.items.length === 0 && <tr><td colSpan={11} className="py-8 text-center muted">No assessments match.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex items-center justify-end gap-2 text-sm">
            <button className="btn btn-line min-h-0 py-1.5" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="admin-prev">Prev</button>
            <span className="muted">Page {page + 1} / {pages}</span>
            <button className="btn btn-line min-h-0 py-1.5" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} data-testid="admin-next">Next</button>
          </div>
        </TabsContent>
        <TabsContent value="teams" className="mt-4"><Teams teams={teams} /></TabsContent>
      </Tabs>
      <AdminCompareDialog row={comparing} onClose={() => setComparing(null)} />
    </div>
  );
}
